import type { AccessLevels } from '@awork/features/workspace/models/permissions.model'
import type { DocumentContributor } from './document-contributor.model'
import type { DocumentTeamAccess } from './document-team-access.model'
import type { DocumentSpace } from './document-space.model'

export enum DocumentSection {
  Project = 'project',
  Spaces = 'spaces',
  Favorites = 'favorites',
  Shared = 'shared',
  Private = 'private',
  Trash = 'trash'
}

export interface IDocument {
  id: string
  currentDocumentVersionId: string
  name?: string
  emoji?: string
  isPrivate: boolean
  documentSpaceId?: string
  documentSpace?: DocumentSpace
  parentId?: string
  projectId?: string
  taskId?: string
  meetingCaptureId?: string | null
  rootDocumentCreatedBy: string
  createdBy: string
  createdOn: Date
  updatedBy: string
  updatedOn: Date
  movedToTrashBy: string
  movedToTrashOn: Date
  isExternal: boolean
  workspaceId: string
  content?: string
  order: number
  depth?: number
  hasChildren?: boolean
  childrenIds?: string[]
  isFirstChild?: boolean
  isLastChild?: boolean
  isSubsetRoot?: boolean
  section?: DocumentSection
  workspaceAccessLevel?: AccessLevels
  contributors: DocumentContributor[]
  teams: DocumentTeamAccess[]
  inheritedWorkspaceAccessFromDocument?: {
    workspaceAccessLevel?: AccessLevels
  }
}

/**
 * The document model
 */
export class Document implements IDocument {
  id: string
  currentDocumentVersionId: string
  name?: string
  emoji?: string
  isPrivate: boolean
  documentSpaceId?: string
  documentSpace?: DocumentSpace
  parentId?: string
  projectId?: string
  taskId?: string
  meetingCaptureId?: string | null
  rootDocumentCreatedBy: string
  createdBy: string
  createdOn: Date
  updatedBy: string
  updatedOn: Date
  movedToTrashBy: string
  movedToTrashOn: Date
  isExternal: boolean
  workspaceId: string
  content?: string
  order: number
  depth?: number
  hasChildren?: boolean
  childrenIds?: string[]
  isFirstChild?: boolean
  isLastChild?: boolean
  isSubsetRoot?: boolean
  section?: DocumentSection
  workspaceAccessLevel?: AccessLevels
  contributors: DocumentContributor[]
  teams: DocumentTeamAccess[]
  inheritedWorkspaceAccessFromDocument?: {
    workspaceAccessLevel?: AccessLevels
  }
  typeName = 'Document'

  constructor(document: Partial<IDocument>) {
    Object.assign(this, document)

    if (this.createdOn) {
      this.createdOn = new Date(this.createdOn)
    }

    if (this.updatedOn) {
      this.updatedOn = new Date(this.updatedOn)
    }

    if (this.movedToTrashOn) {
      this.movedToTrashOn = new Date(this.movedToTrashOn)
    }
  }

  static isDocument(doc: unknown): doc is Document {
    return (doc as Document)?.typeName === 'Document'
  }

  /**
   * Gets the route URL for the document
   * @returns {string[]}
   */
  get routeUrl(): string[] {
    if (this.section === DocumentSection.Favorites || this.section === DocumentSection.Shared) {
      return ['/docs', this.section, this.id]
    }

    return this.projectId ? ['/projects', this.projectId, 'docs', this.id] : ['/docs', this.id]
  }

  /**
   * Gets if the document is trashed
   * @returns {boolean}
   */
  get isTrashed(): boolean {
    return !!this.movedToTrashOn
  }
}
